/**
 * CriptoHomer — cliente web.
 *
 * O transporte NAO e este app. Aqui a mensagem e cifrada e disfarcada; sair
 * daqui e com voce, por WhatsApp, Telegram, SMS ou papel. Por isso a area de
 * composicao e feita de copiar e colar, e nao de "enviar": o app nunca fala
 * com o mensageiro, e o servidor so guarda chave publica.
 *
 * O que o servidor ve esta em docs/04. O que este app NAO esconde — o fato de
 * voce ter falado com alguem, quando e com que volume — aparece na tela, nao
 * so na documentacao.
 *
 * Este arquivo e SO a interface. Cripto em crypto-core, disfarce em disguise,
 * rede em api.ts, persistencia em store.ts. Nenhuma chave e nenhum estado de
 * ratchet e manipulado aqui.
 */
import {
  ContactTrust,
  DecryptionFailedError,
  FingerprintMismatchError,
  MessageType,
  acceptSession,
  computeFingerprint,
  fingerprintFromText,
  initCrypto,
  openFrame,
  sealFrame,
  startSession,
  toBase64Url,
} from '@criptohomer/crypto-core'
import { DisguiseKind, decode as desdisfarcar, encode as disfarcar } from '@criptohomer/disguise'
import { comprimir, descomprimir } from './comprimir.js'
import { ApiError, RateLimitedError, WorkerApi } from './api.js'
import { decodeInvite, encodeInvite, isInvite } from './invite.js'
import { Cofre, type Contato } from './store.js'

const api = new WorkerApi()
let cofre: Cofre | undefined
let contatoAberto: string | undefined

// ── atalhos de DOM ───────────────────────────────────────────────────────────
const $ = <T extends HTMLElement>(id: string): T => {
  const el = document.getElementById(id)
  if (el === null) throw new Error(`elemento #${id} nao existe`)
  return el as T
}
const mostrar = (id: string, visivel: boolean): void => {
  $(id).hidden = !visivel
}

let apagarAviso: ReturnType<typeof setTimeout> | undefined

function aviso(texto: string, tipo: 'erro' | 'ok' | 'neutro' = 'neutro'): void {
  const el = $('aviso')
  el.textContent = texto
  el.className = `aviso ${tipo}`
  el.hidden = texto === ''

  clearTimeout(apagarAviso)
  // Confirmacao some sozinha; erro fica ate a proxima acao. Quem errou
  // precisa de tempo para ler o motivo — quem acertou, nao.
  if (texto !== '' && tipo === 'ok') {
    apagarAviso = setTimeout(() => {
      $('aviso').hidden = true
    }, 4000)
  }
}

/** Erro vira frase util, nunca stack trace na cara da pessoa. */
function explicar(erro: unknown): string {
  if (erro instanceof RateLimitedError) {
    return 'O servidor limitou as requisições deste IP. Tente de novo na próxima hora cheia.'
  }
  if (erro instanceof FingerprintMismatchError) {
    return 'As chaves que o servidor devolveu NÃO batem com esse contato. Não abri a sessão — é exatamente o que essa checagem existe para impedir.'
  }
  if (erro instanceof DecryptionFailedError) {
    return 'Não consegui abrir. Ou a mensagem não é para você, ou chegou fora de ordem, ou foi alterada no caminho.'
  }
  if (erro instanceof ApiError) return erro.reason
  return erro instanceof Error ? erro.message : String(erro)
}

async function comAviso(trabalho: () => Promise<void>, ocupado: string): Promise<void> {
  aviso(ocupado)
  try {
    await trabalho()
  } catch (erro) {
    aviso(explicar(erro), 'erro')
  }
}

// ── telas ────────────────────────────────────────────────────────────────────
function irPara(tela: 'entrada' | 'principal'): void {
  mostrar('carregando', false)
  mostrar('tela-entrada', tela === 'entrada')
  mostrar('tela-principal', tela === 'principal')
}

function contatoAtual(): Contato | undefined {
  return contatoAberto === undefined ? undefined : cofre?.contatos.get(contatoAberto)
}

function desenharIdentidade(): void {
  if (cofre === undefined) return
  $('meu-fingerprint').textContent = cofre.identity.fingerprintText
}

function desenharContatos(): void {
  if (cofre === undefined) return
  const lista = $('lista-contatos')
  lista.replaceChildren()

  if (cofre.contatos.size === 0) {
    const vazio = document.createElement('p')
    vazio.className = 'vazio'
    vazio.textContent = 'Nenhuma conversa ainda.'
    lista.append(vazio)
    return
  }

  for (const [fp, c] of cofre.contatos) {
    const item = document.createElement('button')
    item.type = 'button'
    item.className = fp === contatoAberto ? 'contato ativo' : 'contato'

    const nome = document.createElement('span')
    nome.className = 'contato-nome'
    nome.textContent = c.apelido

    const ultima = c.conversa.at(-1)
    if (ultima !== undefined) {
      const previa = document.createElement('span')
      previa.className = 'contato-previa'
      previa.textContent = `${ultima.minha ? 'Você: ' : ''}${ultima.texto}`
      nome.append(previa)
    }

    const marca = document.createElement('span')
    const verificado = c.confianca === ContactTrust.Verificado
    marca.className = verificado ? 'selo ok' : 'selo alerta'
    marca.textContent = verificado ? 'ok' : '!'

    item.append(nome, marca)
    item.addEventListener('click', () => {
      abrirConversa(fp)
    })
    lista.append(item)
  }
}

function abrirConversa(fp: string): void {
  contatoAberto = fp
  // No celular a conversa cobre a lista; no desktop as duas convivem.
  $('tela-principal').classList.add('conversando')
  mostrar('caixa-codigo', false)
  mostrar('caixa-saida', false)
  trocarAba('escrever')
  desenharContatos()
  desenharConversa()
}

function voltarParaLista(): void {
  $('tela-principal').classList.remove('conversando')
}

function desenharConversa(): void {
  const c = contatoAtual()
  mostrar('painel-conversa', c !== undefined)
  mostrar('painel-vazio', c === undefined)
  if (c === undefined) return

  $('conversa-titulo').textContent = c.apelido
  $('codigo-seguranca').textContent = c.sessao.safetyCode.words.join(' · ')

  const selo = $('conversa-confianca')
  const verificado = c.confianca === ContactTrust.Verificado
  selo.textContent = verificado ? 'verificado' : 'não verificado'
  selo.className = verificado ? 'selo ok' : 'selo alerta'
  mostrar('botao-verificar', !verificado)

  const linhas = $('mensagens')
  linhas.replaceChildren()

  if (c.conversa.length === 0) {
    const vazio = document.createElement('p')
    vazio.className = 'conversa-vazia'
    vazio.textContent =
      'Nada ainda. Escreva abaixo, copie os emojis e mande pelo canal que quiser — o app não envia nada sozinho.'
    linhas.append(vazio)
    return
  }

  for (const m of c.conversa) {
    const linha = document.createElement('div')
    linha.className = m.minha ? 'msg minha' : 'msg dele'
    const texto = document.createElement('p')
    texto.textContent = m.texto
    const hora = document.createElement('time')
    hora.textContent = new Date(m.em).toLocaleTimeString('pt-BR', {
      hour: '2-digit',
      minute: '2-digit',
    })
    linha.append(texto, hora)
    linhas.append(linha)
  }
  linhas.scrollTop = linhas.scrollHeight
}

function redesenhar(): void {
  desenharIdentidade()
  desenharContatos()
  desenharConversa()
}

function trocarAba(qual: 'escrever' | 'receber'): void {
  const escrevendo = qual === 'escrever'
  mostrar('painel-escrever', escrevendo)
  mostrar('painel-receber', !escrevendo)
  $('aba-escrever').classList.toggle('ativa', escrevendo)
  $('aba-receber').classList.toggle('ativa', !escrevendo)
}

// ── entrada: criar ou abrir o cofre ──────────────────────────────────────────
async function publicarNoServidor(): Promise<void> {
  if (cofre === undefined) return
  const spk = cofre.preKeys.signedPreKey
  await api.registrarIdentidade(cofre.identity, {
    keyId: spk.keyId,
    publicKey: spk.publicKey,
    signature: spk.signature,
  })
  const disponiveis = await api.contarPreChaves(cofre.identity.fingerprintText)
  if (disponiveis === 0) {
    const lote = cofre.preKeys.replenishOneTimePreKeys(20)
    await api.publicarPreChaves(cofre.identity, lote)
    cofre.salvar()
  }
}

async function criarCofre(): Promise<void> {
  const campo = $<HTMLInputElement>('frase')
  if (campo.value.length < 8) {
    aviso(
      'Use uma frase de pelo menos 8 caracteres. Ela é a única coisa entre o seu cofre e quem pegar este aparelho.',
      'erro',
    )
    return
  }
  const frase = new TextEncoder().encode(campo.value)
  await comAviso(async () => {
    cofre = Cofre.criar(frase, Math.floor(Date.now() / 1000))
    // Publica ANTES de salvar: se o servidor recusar, nao fica cofre orfao
    // registrado so aqui, com pre-chaves que ninguem consegue buscar.
    await publicarNoServidor()
    cofre.salvar()
    campo.value = ''
    irPara('principal')
    redesenhar()
    aviso('Identidade criada. Passe seu código para quem for falar com você.', 'ok')
  }, 'Gerando identidade e publicando pré-chaves…')
}

async function abrirCofre(): Promise<void> {
  const campo = $<HTMLInputElement>('frase')
  const frase = new TextEncoder().encode(campo.value)
  await comAviso(async () => {
    cofre = Cofre.abrir(frase)
    campo.value = ''
    irPara('principal')
    redesenhar()
    aviso('')
    // Repor estoque e barato e evita descobrir que acabou na hora errada.
    await publicarNoServidor().catch(() => undefined)
  }, 'Abrindo o cofre…')
}

// ── pareamento ───────────────────────────────────────────────────────────────
async function adicionarContato(): Promise<void> {
  // Referencia local: dentro do callback assincrono o TypeScript nao tem como
  // saber que `cofre` continua definido, e afirmar que sim com `!` seria
  // mentir para o compilador justamente onde ha um await no meio.
  const meu = cofre
  if (meu === undefined) return
  const fp = $<HTMLInputElement>('fp-contato').value.trim()
  const apelido = $<HTMLInputElement>('apelido-contato').value.trim() || fp.slice(0, 8)

  if (fp === meu.identity.fingerprintText) {
    aviso('Esse é o seu próprio código.', 'erro')
    return
  }
  if (meu.contatos.has(fp)) {
    aviso('Essa conversa já existe.', 'erro')
    return
  }

  await comAviso(async () => {
    const bundle = await api.buscarBundle(fp)
    // startSession confere a amarracao: se as chaves nao hasheiam para este
    // fingerprint, lanca ANTES de qualquer DH.
    const sessao = startSession(meu.identity, bundle, fingerprintFromText(fp))
    meu.contatos.set(fp, {
      apelido,
      sessao,
      confianca: ContactTrust.NaoVerificado,
      conversa: [],
      convitePendente: sessao.initialMessage,
    })
    meu.salvar()
    $<HTMLInputElement>('fp-contato').value = ''
    $<HTMLInputElement>('apelido-contato').value = ''
    mostrar('form-adicionar', false)
    abrirConversa(fp)
    aviso('Conversa criada. Confira as 6 palavras por outro canal antes de confiar.', 'ok')
  }, 'Buscando o bundle…')
}

// ── conversa ─────────────────────────────────────────────────────────────────
/** O codec escolhido na hora de cifrar. */
function codecEscolhido(): DisguiseKind {
  const v = $<HTMLSelectElement>('codec').value
  if (v === DisguiseKind.Compact) return DisguiseKind.Compact
  if (v === DisguiseKind.UnicodeWords) return DisguiseKind.UnicodeWords
  return DisguiseKind.Emoji
}

async function cifrar(): Promise<void> {
  const c = contatoAtual()
  if (cofre === undefined || c === undefined) return
  const texto = $<HTMLTextAreaElement>('entrada-texto').value
  if (texto.trim() === '') return

  try {
    // Comprimir ANTES de cifrar. O bit vai no prefixo, que entra no AD:
    // quem recebe sabe se deve descomprimir sem confiar em heuristica.
    const claro = await comprimir(new TextEncoder().encode(texto))
    const quadro = sealFrame(
      c.sessao.ratchet,
      c.sessao.associatedData,
      { type: MessageType.Text, flags: claro.flags, ttlSeconds: 0 },
      claro.bytes,
    )
    // A primeira mensagem leva o material do X3DH junto; as seguintes, nao.
    const bytes =
      c.convitePendente === undefined
        ? quadro
        : encodeInvite({ initialMessage: c.convitePendente, frame: quadro })

    const disfarcado = disfarcar(bytes, codecEscolhido())
    // O codec emoji sempre devolve texto; o tipo e uniao por causa do media.
    if (disfarcado.transport !== 'text') throw new Error('codec devolveu arquivo, não texto')
    $<HTMLTextAreaElement>('saida-disfarcada').value = disfarcado.text
    prepararEnvio(disfarcado.text)
    mostrar('caixa-saida', true)

    c.conversa.push({ minha: true, texto, em: Date.now() })
    delete c.convitePendente
    cofre.salvar()
    $<HTMLTextAreaElement>('entrada-texto').value = ''
    desenharConversa()
    desenharContatos()
    aviso('Pronto. Copie os emojis e mande pelo canal que quiser.', 'ok')
  } catch (erro) {
    aviso(explicar(erro), 'erro')
  }
}

/**
 * Prepara os atalhos para os mensageiros.
 *
 * Nenhum deles fala com o WhatsApp ou o Telegram: `wa.me` e `t.me` sao
 * esquemas que o SISTEMA resolve, abrindo o app instalado com o texto ja
 * preenchido. Este app nunca ve a sua conta, e continua nao enviando nada —
 * quem aperta "enviar" no mensageiro e voce.
 *
 * `navigator.share` e melhor onde existe: abre a folha do sistema com TODOS
 * os apps, nao so os dois, e nao passa o texto por uma URL. Onde nao existe
 * (a maioria dos navegadores de desktop), os links cobrem o caso.
 */
function prepararEnvio(texto: string): void {
  const codificado = encodeURIComponent(texto)
  $<HTMLAnchorElement>('link-whatsapp').href = `https://wa.me/?text=${codificado}`
  $<HTMLAnchorElement>('link-telegram').href = `https://t.me/share/url?url=&text=${codificado}`
  mostrar('botao-compartilhar', typeof navigator.share === 'function')
}

async function compartilhar(): Promise<void> {
  const texto = $<HTMLTextAreaElement>('saida-disfarcada').value
  if (texto === '') return
  try {
    await navigator.share({ text: texto })
  } catch (erro) {
    // Cancelar a folha de compartilhamento lanca AbortError. Nao e falha, e
    // avisar "erro" para quem so mudou de ideia seria ruido.
    if (erro instanceof DOMException && erro.name === 'AbortError') return
    aviso('Não consegui abrir o compartilhamento. Use "Copiar" e cole no app.', 'erro')
  }
}

async function decifrar(): Promise<void> {
  if (cofre === undefined) return
  const entrada = $<HTMLTextAreaElement>('entrada-disfarcada').value.trim()
  if (entrada === '') return

  try {
    const bytes = desdisfarcar(entrada)

    if (isInvite(bytes)) {
      await abrirConvite(bytes)
      return
    }

    const c = contatoAtual()
    if (c === undefined) {
      aviso('Abra a conversa de quem mandou essa mensagem.', 'erro')
      return
    }
    const aberto = openFrame(c.sessao.ratchet, c.sessao.associatedData, bytes)
    c.conversa.push({
      minha: false,
      texto: new TextDecoder().decode(await descomprimir(aberto.plaintext, aberto.flags)),
      em: Date.now(),
    })
    cofre.salvar()
    $<HTMLTextAreaElement>('entrada-disfarcada').value = ''
    desenharConversa()
    desenharContatos()
    trocarAba('escrever')
    aviso('')
  } catch (erro) {
    aviso(explicar(erro), 'erro')
  }
}

/**
 * Convite recebido: refaz o X3DH do lado de quem responde.
 *
 * O fingerprint do remetente e CALCULADO das chaves que vieram no convite —
 * e por isso ele nao prova nada sozinho: quem forja o convite forja as duas
 * coisas junto. Ele serve para enderecar o contato e derivar o codigo de 6
 * palavras; quem prova a identidade e a conferencia desse codigo por outro
 * canal, e a UI insiste nisso.
 */
async function abrirConvite(bytes: Uint8Array): Promise<void> {
  if (cofre === undefined) return
  const { initialMessage, frame } = decodeInvite(bytes)

  const fpRemetente = computeFingerprint(
    initialMessage.initiatorSignPublicKey,
    initialMessage.initiatorAgreePublicKey,
  )
  const fp = toBase64Url(fpRemetente)

  const sessao = acceptSession(cofre.identity, cofre.preKeys, initialMessage, fpRemetente)
  const aberto = openFrame(sessao.ratchet, sessao.associatedData, frame)
  const texto = new TextDecoder().decode(await descomprimir(aberto.plaintext, aberto.flags))

  const existente = cofre.contatos.get(fp)
  cofre.contatos.set(fp, {
    apelido: existente?.apelido ?? fp.slice(0, 8),
    sessao,
    confianca: existente?.confianca ?? ContactTrust.NaoVerificado,
    conversa: [...(existente?.conversa ?? []), { minha: false, texto, em: Date.now() }],
  })
  cofre.salvar()
  $<HTMLTextAreaElement>('entrada-disfarcada').value = ''
  abrirConversa(fp)
  aviso('Convite aceito e mensagem aberta. Confira as 6 palavras antes de confiar.', 'ok')
}

function marcarVerificado(): void {
  const c = contatoAtual()
  if (cofre === undefined || c === undefined) return
  c.confianca = ContactTrust.Verificado
  cofre.salvar()
  desenharConversa()
  desenharContatos()
  aviso('Contato marcado como verificado.', 'ok')
}

function autodestruir(): void {
  if (cofre === undefined) return
  if (!confirm('Apagar identidade, contatos e conversas deste navegador? Não dá para desfazer.')) {
    return
  }
  cofre.destruir()
  cofre = undefined
  contatoAberto = undefined
  location.reload()
}

async function copiar(id: string): Promise<void> {
  const el = $(id)
  const texto =
    el instanceof HTMLInputElement || el instanceof HTMLTextAreaElement
      ? el.value
      : (el.textContent ?? '')
  try {
    await navigator.clipboard.writeText(texto)
    aviso('Copiado.', 'ok')
  } catch {
    // Sem permissao de area de transferencia (http, aba restrita, iframe):
    // selecionar o texto deixa a pessoa copiar a mao em vez de travar.
    if (el instanceof HTMLTextAreaElement || el instanceof HTMLInputElement) el.select()
    aviso('Não consegui copiar sozinho. O texto está selecionado — copie com Ctrl+C.', 'erro')
  }
}

/** Aviso de frase fraca. Informa, nao bloqueia. */
function avaliarFrase(): void {
  const valor = $<HTMLInputElement>('frase').value
  const el = $('forca-frase')
  if (valor === '') {
    el.hidden = true
    return
  }
  el.hidden = false
  if (valor.length < 8) {
    el.textContent = `Curta demais — faltam ${8 - valor.length}.`
    el.className = 'forca alerta'
  } else if (valor.length < 16) {
    el.textContent = 'Serve. Quatro palavras seriam bem mais difíceis de quebrar.'
    el.className = 'forca ajuda'
  } else {
    el.textContent = 'Boa.'
    el.className = 'forca ok-texto'
  }
}

/**
 * Diz em voz alta a troca de cada codec.
 *
 * Densidade e discricao sao opostas, e a pessoa precisa escolher sabendo:
 * o compacto cabe em metade dos caracteres e NAO passa por brincadeira.
 */
function explicarCodec(): void {
  const dicas: Record<string, string> = {
    [DisguiseKind.Emoji]: 'Mais longo, porém parece uma brincadeira com figurinhas.',
    [DisguiseKind.Compact]:
      'Cerca de metade dos caracteres. Em compensação, um bloco de ideogramas não passa despercebido — use quando o que importa é caber.',
    [DisguiseKind.UnicodeWords]: 'Parece um texto sem sentido. É o mais longo dos três, de longe.',
  }
  $('dica-codec').textContent = dicas[$<HTMLSelectElement>('codec').value] ?? ''
}

/**
 * Registra o service worker. Falhar aqui NAO pode derrubar o app: sem ele o
 * app so deixa de funcionar offline, e um erro de registro (http, aba
 * privada, permissao negada) nao e motivo para nao abrir a conversa.
 */
function registrarOffline(): void {
  if (!('serviceWorker' in navigator)) return
  navigator.serviceWorker.register('./sw.js').catch(() => {
    // Silencio proposital: nao ha nada que a pessoa possa fazer a respeito.
  })
}

// ── inicio ───────────────────────────────────────────────────────────────────
async function iniciar(): Promise<void> {
  registrarOffline()
  await initCrypto()
  irPara('entrada')

  const temCofre = Cofre.existe()
  $('titulo-entrada').textContent = temCofre ? 'Bem-vindo de volta' : 'CriptoHomer'
  $('subtitulo-entrada').textContent = temCofre
    ? 'Digite a frase para abrir seu cofre.'
    : 'Mensagens cifradas que passam por qualquer canal.'
  mostrar('botao-criar', !temCofre)
  mostrar('botao-abrir', temCofre)
  mostrar('explicacao-primeira-vez', !temCofre)

  $('botao-criar').addEventListener('click', () => void criarCofre())
  $('botao-abrir').addEventListener('click', () => void abrirCofre())
  $('botao-adicionar').addEventListener('click', () => void adicionarContato())
  $('botao-abrir-adicionar').addEventListener('click', () => {
    const form = $('form-adicionar')
    form.hidden = !form.hidden
    if (!form.hidden) $<HTMLInputElement>('fp-contato').focus()
  })
  $('botao-cifrar').addEventListener('click', () => void cifrar())
  $('botao-decifrar').addEventListener('click', () => void decifrar())
  $('codec').addEventListener('change', explicarCodec)
  $('botao-verificar').addEventListener('click', marcarVerificado)
  $('botao-destruir').addEventListener('click', autodestruir)
  $('botao-voltar').addEventListener('click', voltarParaLista)
  $('botao-codigo').addEventListener('click', () => {
    const caixa = $('caixa-codigo')
    caixa.hidden = !caixa.hidden
  })
  $('aba-escrever').addEventListener('click', () => {
    trocarAba('escrever')
  })
  $('aba-receber').addEventListener('click', () => {
    trocarAba('receber')
  })
  $('copiar-fingerprint').addEventListener('click', () => void copiar('meu-fingerprint'))
  $('copiar-saida').addEventListener('click', () => void copiar('saida-disfarcada'))
  $('botao-compartilhar').addEventListener('click', () => void compartilhar())
  explicarCodec()
  $<HTMLInputElement>('frase').addEventListener('input', avaliarFrase)
  $<HTMLInputElement>('frase').addEventListener('keydown', (e) => {
    if (e.key === 'Enter') void (temCofre ? abrirCofre() : criarCofre())
  })
  // Enter manda, Shift+Enter quebra linha — o que todo mensageiro faz.
  $<HTMLTextAreaElement>('entrada-texto').addEventListener('keydown', (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      void cifrar()
    }
  })
}

void iniciar()
